/**
 * 구독 결제 성공 페이지
 * Polar 체크아웃 완료 후 리다이렉트되는 페이지
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('결제를 확인하고 있습니다...');

  useEffect(() => {
    verifyCheckout();
  }, []);

  const verifyCheckout = async () => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setStatus('error');
      setMessage('세션 정보가 없습니다.');
      return;
    }

    try {
      const response = await axios.post('/api/subscription/verify-checkout', {
        sessionId,
      });

      if (response.data.success) {
        setStatus('success');
        setMessage('구독이 활성화되었습니다! 스탠다드 플랜을 이용하실 수 있습니다.');
        // 사용자 정보 새로고침
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        setStatus('error');
        setMessage(response.data.error || '결제 확인에 실패했습니다.');
      }
    } catch (err) {
      // 웹훅으로 이미 처리되었을 수 있으므로 사용자 정보 새로고침 시도
      if (refreshUser) {
        await refreshUser();
      }
      setStatus('success');
      setMessage('결제가 완료되었습니다! 구독이 활성화됩니다.');
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <Paper sx={{ p: 5, textAlign: 'center', maxWidth: 500 }}>
        {status === 'verifying' && (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">{message}</Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              결제 완료!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {message}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/dashboard')}>
              대시보드로 이동
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              확인 실패
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {message}
            </Alert>
            <Button variant="outlined" onClick={() => navigate('/mypage')}>
              마이페이지로 이동
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
